import { UseFormReturn } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { HighlightEditor } from '@/components/ui/highlight-editor';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { HighlightableSelect } from '@/components/ui/highlightable-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ResponsiveImageUpload } from '../shared/responsive-image-upload';
import { useState, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { uploadSiteSurveyPhoto } from '../shared/site-survey-photo-upload';

interface DcAdditionalWorksProps {
  form: UseFormReturn<any>;
  installationId?: number;
  surveyId?: number;
  onPhotoUploaded?: () => void;
}

export function DcAdditionalWorks({
  form,
  installationId,
  surveyId,
  onPhotoUploaded,
}: DcAdditionalWorksProps) {
  const [uploadingImages, setUploadingImages] = useState<Record<string, boolean>>({});
  const { toast } = useToast();
  const fileInputRefs = {
    dcRouteImage1: useRef<HTMLInputElement>(null),
    dcRouteImage2: useRef<HTMLInputElement>(null),
    dcRouteImage3: useRef<HTMLInputElement>(null),
    earthPitLocationImage: useRef<HTMLInputElement>(null),
  };

  const isMobileDevice = () => {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
    );
  };

  const handleImageUpload = async (field: string, file: File) => {
    setUploadingImages((prev) => ({ ...prev, [field]: true }));

    try {
      await uploadSiteSurveyPhoto({
        form,
        field,
        file,
        surveyId,
        installationId,
        customerId: form.getValues('customerId'),
        description: `DC Works ${field} photo`,
        toast,
        onPhotoUploaded,
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description:
          error instanceof Error ? error.message : 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingImages((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleFileSelect = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(field, file);
    }
    event.target.value = '';
  };

  const handleCameraCapture = (field: string) => {
    const input = fileInputRefs[field as keyof typeof fileInputRefs];
    if (input.current) {
      if (isMobileDevice()) {
        input.current.setAttribute('capture', 'environment');
      } else {
        input.current.removeAttribute('capture');
      }
      input.current.click();
    }
  };

  const triggerFileUpload = (field: string) => {
    const input = fileInputRefs[field as keyof typeof fileInputRefs];
    if (input.current) {
      input.current.removeAttribute('capture');
      input.current.click();
    }
  };

  const renderImageUpload = (fieldName: string, label: string) => {
    return (
      <ResponsiveImageUpload
        form={form}
        fieldName={fieldName}
        label={label}
        isUploading={uploadingImages[fieldName]}
        onCameraCapture={() => handleCameraCapture(fieldName)}
        onFileUpload={() => triggerFileUpload(fieldName)}
        onPhotoDeleted={onPhotoUploaded}
      />
    );
  };

  return (
    <div className="space-y-6">
      {Object.entries(fileInputRefs).map(([key, ref]) => (
        <input
          key={key}
          ref={ref}
          type="file"
          accept="image/*,.heic,.heif"
          style={{ display: 'none' }}
          onChange={handleFileSelect(key)}
        />
      ))}

      <Card>
        <CardHeader>
          <CardTitle>DC Runs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="dcStrings"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Strings</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select number of strings"
                      options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => ({
                        value: num.toString(),
                        label: num.toString(),
                      }))}
                      fieldName="dcStrings"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dcCableCsa"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cable CSA</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select cable CSA"
                      options={[
                        { value: '4mm', label: '4mm' },
                        { value: '6mm', label: '6mm' },
                      ]}
                      fieldName="dcCableCsa"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dcLongestRun"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Longest Run</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select longest run"
                      options={[5, 10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 100].map((length) => ({
                        value: `${length}m`,
                        label: `${length}m`,
                      }))}
                      fieldName="dcLongestRun"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-medium">DC Route Images</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {renderImageUpload('dcRouteImage1', 'DC Route Image 1')}
              {renderImageUpload('dcRouteImage2', 'DC Route Image 2')}
              {renderImageUpload('dcRouteImage3', 'DC Route Image 3')}
            </div>
          </div>

          <FormField
            control={form.control}
            name="scopeOfWorksDcRuns"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Scope of Works – DC Runs</FormLabel>
                <FormControl>
                  <HighlightEditor
                    value={field.value || ''}
                    onChange={field.onChange}
                    placeholder="Describe the scope of works for DC runs..."
                    savedStatus={!!surveyId}
                    minHeight="150px"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Other Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <FormField
                control={form.control}
                name="evChargerRequired"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3">
                    <FormControl>
                      <Checkbox checked={field.value || false} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="font-medium !mt-0">EV Charger</FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="evChargerDetails"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <HighlightEditor
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="EV Charger details..."
                        savedStatus={!!surveyId}
                        minHeight="60px"
                        disabled={!form.watch('evChargerRequired')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <FormField
                control={form.control}
                name="immersionCircuitRequired"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3">
                    <FormControl>
                      <Checkbox checked={field.value || false} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="font-medium !mt-0">Immersion Circuit iBoost</FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="immersionCircuitDetails"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <HighlightEditor
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Immersion circuit details..."
                        savedStatus={!!surveyId}
                        minHeight="60px"
                        disabled={!form.watch('immersionCircuitRequired')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <FormField
                control={form.control}
                name="birdguardRequired"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3">
                    <FormControl>
                      <Checkbox checked={field.value || false} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="font-medium !mt-0">Birdguard</FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="birdguardDetails"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <HighlightEditor
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Birdguard details..."
                        savedStatus={!!surveyId}
                        minHeight="60px"
                        disabled={!form.watch('birdguardRequired')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <FormField
                control={form.control}
                name="gatewayRequired"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3">
                    <FormControl>
                      <Checkbox checked={field.value || false} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="font-medium !mt-0">Gateway</FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="gatewayDetails"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <HighlightEditor
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Gateway details..."
                        savedStatus={!!surveyId}
                        minHeight="60px"
                        disabled={!form.watch('gatewayRequired')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Earthing and Ground Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="gatewayEarthing"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Gateway Earthing</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select earthing type"
                      options={[
                        { value: 'EARTH_PIT_ROD_CLAMP', label: 'Earth Pit/Rod/Clamp' },
                        { value: 'NOT_REQUIRED', label: 'Not Required' },
                        { value: 'REQUIRED', label: 'Required' },
                      ]}
                      fieldName="gatewayEarthing"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {renderImageUpload('earthPitLocationImage', 'Earth Pit Location Image')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="groundTools"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ground Tools</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select option"
                      options={[
                        { value: 'NO', label: 'No' },
                        { value: 'REQUIRED', label: 'Required' },
                      ]}
                      fieldName="groundTools"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="diggerHire"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Digger Hire</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select option"
                      options={[
                        { value: 'NO', label: 'No' },
                        { value: 'REQUIRED', label: 'Required' },
                      ]}
                      fieldName="diggerHire"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="scopeOfWorksGroundWorks"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Scope of Works – Ground Works</FormLabel>
                <FormControl>
                  <HighlightEditor
                    value={field.value || ''}
                    onChange={field.onChange}
                    placeholder="Describe the scope of works for ground works..."
                    savedStatus={!!surveyId}
                    minHeight="200px"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
